import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ReactComponent as InfoTooltipIcon } from '@assets/images/Input/info-tooltip.svg'
import type {
  AutocompleteOption,
  AutocompleteProps,
} from './Autocomplete.types'
import {
  AUTOCOMPLETE_DEMO_ERROR_MESSAGE,
  AUTOCOMPLETE_DEMO_FILLED_VALUE,
  AUTOCOMPLETE_DEMO_INFO_MESSAGE,
  AUTOCOMPLETE_DEMO_LABEL,
  AUTOCOMPLETE_DEMO_OPTIONS,
  AUTOCOMPLETE_DEMO_PLACEHOLDER,
  AUTOCOMPLETE_DEMO_QUERY,
} from './Autocomplete.types'
import {
  filterAutocompleteOptions,
  highlightAutocompleteMatches,
} from './Autocomplete.utils'
import './Autocomplete.css'

const Autocomplete: React.FC<AutocompleteProps> = ({
  label = AUTOCOMPLETE_DEMO_LABEL,
  placeholder = AUTOCOMPLETE_DEMO_PLACEHOLDER,
  options = AUTOCOMPLETE_DEMO_OPTIONS,
  value,
  defaultValue = '',
  allowCustom = false,
  disabled = false,
  error = false,
  helperText,
  optional = false,
  showTooltip = false,
  className = '',
  darkMode = false,
  previewState,
  onChange,
}) => {
  const inputId = useId()
  const listboxId = useId()
  const fieldRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isError = error || previewState === 'error'
  const isOptional = optional || previewState === 'optional'
  const hasTooltip = showTooltip || previewState === 'with-tooltip'

  const resolvedValue = useMemo(() => {
    if (isPreview) {
      if (previewState === 'expanded') {
        return AUTOCOMPLETE_DEMO_QUERY
      }

      if (
        previewState === 'filled' ||
        previewState === 'disabled' ||
        previewState === 'error' ||
        previewState === 'with-info'
      ) {
        return AUTOCOMPLETE_DEMO_FILLED_VALUE
      }

      return ''
    }

    return value ?? internalValue
  }, [internalValue, isPreview, previewState, value])

  const resolvedHelperText = useMemo(() => {
    if (helperText != null) {
      return helperText
    }

    if (previewState === 'error') {
      return AUTOCOMPLETE_DEMO_ERROR_MESSAGE
    }

    if (previewState === 'with-info') {
      return AUTOCOMPLETE_DEMO_INFO_MESSAGE
    }

    return undefined
  }, [helperText, previewState])

  const filteredOptions = useMemo(
    () => filterAutocompleteOptions(options, resolvedValue),
    [options, resolvedValue],
  )

  const displayOptions = useMemo(() => {
    if (isPreview && previewState === 'expanded') {
      return filterAutocompleteOptions(options, AUTOCOMPLETE_DEMO_QUERY)
    }

    if (isPreview) {
      return options
    }

    return filteredOptions
  }, [filteredOptions, isPreview, options, previewState])

  const highlightQuery =
    isPreview && previewState === 'expanded'
      ? AUTOCOMPLETE_DEMO_QUERY
      : resolvedValue

  const isMenuVisible =
    isPreview && previewState === 'expanded' ? true : menuOpen

  const isOpenTrigger =
    isMenuVisible && (previewState === 'expanded' || (!isPreview && menuOpen))

  const emitChange = useCallback(
    (nextValue: string) => {
      if (value === undefined) {
        setInternalValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [onChange, value],
  )

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    setActiveIndex(-1)
  }, [])

  /** Chevron / keyboard can browse the full list; focus alone does not open suggestions. */
  const handleToggleMenu = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen((open) => {
      if (open) {
        setActiveIndex(-1)
        return false
      }

      return true
    })
    inputRef.current?.focus()
  }, [isDisabled, isPreview])

  const handleSelectOption = useCallback(
    (option: AutocompleteOption) => {
      emitChange(option.label)
      closeMenu()
      inputRef.current?.focus()
    },
    [closeMenu, emitChange],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isPreview || isDisabled) {
        return
      }

      const nextValue = event.target.value
      emitChange(nextValue)
      setActiveIndex(-1)

      // Suggestions appear after typing, not on empty focus/click.
      if (nextValue.trim()) {
        setMenuOpen(true)
      } else {
        setMenuOpen(false)
      }
    },
    [emitChange, isDisabled, isPreview],
  )

  const handleInputBlur = useCallback(() => {
    if (isPreview || isDisabled || allowCustom) {
      return
    }

    const exactMatch = options.find(
      (option) =>
        option.label.toLowerCase() === resolvedValue.trim().toLowerCase() ||
        option.value.toLowerCase() === resolvedValue.trim().toLowerCase(),
    )

    if (!exactMatch && resolvedValue) {
      emitChange('')
    }
  }, [allowCustom, emitChange, isDisabled, isPreview, options, resolvedValue])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isPreview || isDisabled) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(0)
          return
        }

        setActiveIndex((index) =>
          displayOptions.length === 0
            ? -1
            : Math.min(index + 1, displayOptions.length - 1),
        )
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(displayOptions.length - 1)
          return
        }

        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      }

      if (event.key === 'Enter' && menuOpen && activeIndex >= 0) {
        event.preventDefault()
        const option = displayOptions[activeIndex]
        if (option) {
          handleSelectOption(option)
        }
        return
      }

      if (event.key === 'Escape' && menuOpen) {
        event.preventDefault()
        closeMenu()
      }
    },
    [
      activeIndex,
      closeMenu,
      displayOptions,
      handleSelectOption,
      isDisabled,
      isPreview,
      menuOpen,
    ],
  )

  useEffect(() => {
    if (isPreview || !menuOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [closeMenu, isPreview, menuOpen])

  const classNames = [
    'ds-autocomplete',
    isDisabled ? 'ds-autocomplete--disabled' : '',
    isError ? 'ds-autocomplete--error' : '',
    darkMode ? 'ds-autocomplete--dark' : '',
    isPreview ? 'ds-autocomplete--preview' : '',
    previewState ? `ds-autocomplete--preview-${previewState}` : '',
    resolvedValue ? 'ds-autocomplete--filled' : '',
    isOpenTrigger ? 'ds-autocomplete--open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      <div className="ds-autocomplete__label-row">
        <label className="ds-autocomplete__label" htmlFor={inputId}>
          {label}
          {isOptional ? (
            <span className="ds-autocomplete__optional"> (Optional)</span>
          ) : null}
        </label>
        {hasTooltip ? (
          <span
            className="ds-autocomplete__tooltip-trigger"
            title={label}
            aria-label={`More information about ${label}`}
          >
            <InfoTooltipIcon />
          </span>
        ) : null}
      </div>

      <div
        className={`ds-autocomplete__field${
          previewState === 'focused'
            ? ' ds-autocomplete__field--focused-preview'
            : ''
        }`}
        ref={fieldRef}
      >
        <div className="ds-autocomplete__control">
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            className="ds-autocomplete__input"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isMenuVisible}
            aria-controls={listboxId}
            aria-activedescendant={
              activeIndex >= 0
                ? `${listboxId}-option-${activeIndex}`
                : undefined
            }
            placeholder={placeholder}
            value={resolvedValue}
            disabled={isPreview || isDisabled}
            tabIndex={isPreview ? -1 : 0}
            autoComplete="off"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className="ds-autocomplete__toggle"
            tabIndex={isPreview ? -1 : 0}
            aria-label={
              isMenuVisible ? 'Close suggestions' : 'Open suggestions'
            }
            aria-expanded={isMenuVisible}
            aria-controls={listboxId}
            disabled={isPreview || isDisabled}
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            onClick={handleToggleMenu}
          >
            <i
              className="mgc mgc_down_small_fill ds-autocomplete__chevron"
              aria-hidden="true"
            />
          </button>
        </div>

        {isMenuVisible ? (
          <div id={listboxId} className="ds-autocomplete__menu" role="listbox">
            <div className="ds-autocomplete__menu-scroll">
              {displayOptions.length === 0 ? (
                <p className="ds-autocomplete__empty">No matching results</p>
              ) : (
                displayOptions.map((option, index) => {
                  const isSelected = resolvedValue === option.label
                  const isActive = index === activeIndex

                  return (
                    <button
                      key={option.value}
                      id={`${listboxId}-option-${index}`}
                      type="button"
                      className={`ds-autocomplete__option${
                        isSelected ? ' ds-autocomplete__option--selected' : ''
                      }${isActive ? ' ds-autocomplete__option--active' : ''}`}
                      role="option"
                      aria-selected={isSelected}
                      disabled={isPreview || isDisabled}
                      onMouseDown={(event) => {
                        event.preventDefault()
                      }}
                      onClick={() => handleSelectOption(option)}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <span className="ds-autocomplete__option-label">
                        {highlightAutocompleteMatches(
                          option.label,
                          highlightQuery,
                        )}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        ) : null}
      </div>

      {resolvedHelperText ? (
        <p
          className={`ds-autocomplete__helper${
            isError ? ' ds-autocomplete__helper--error' : ''
          }`}
        >
          {resolvedHelperText}
        </p>
      ) : null}
    </div>
  )
}

export default Autocomplete
